<template>
  <div id="app">
    <NavBar />
    <div class="container-fluid">
      <div class="row">
        <div class="col col-0 col-sm-1 col-lg-3"></div>
        <div class="col col-12 col-sm-10 col-lg-6">
          <router-view />
        </div>
        <div class="col col-0 col-sm-1 col-lg-3"></div>
      </div>
    </div>
    <footer class="app-footer">
      <div class="footer-divider"></div>
      <div class="container text-center py-3">
        <a class="small-link" href="/terms">Terms of Use</a>
        <span class="footer-dot">&middot;</span>
        <a class="small-link" href="/privacy">Privacy Policy</a>
      </div>
    </footer>
    <div v-if="cookie_consent == null"
         class="fixed-bottom cookie-banner"
         >
      <div class="cookie-inner d-flex justify-content-center align-items-center">
        <p class="mb-0 text-center cookie-text">
          We use cookies to improve our website. Please accept or decline cookies.
        </p>
        <div class="ms-3 d-flex gap-2">
          <button class="btn btn-cookie-accept"
                  @click="save_consent(true)">Accept</button>
          <button class="btn btn-cookie-decline"
                  @click="save_consent(false)">Decline</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { Hub } from 'aws-amplify/utils'
  import NavBar from './generic/NavBar.vue';

  export default {
    name: 'App',
    data() {
      return {
        cookie_consent: null
      }
    },
    methods: {
      save_consent(accepted) {
        localStorage.setItem(
          "cookie_consent",
          JSON.stringify(accepted)
        );
        this.cookie_consent = accepted;

        if (accepted) {
          this.load_analytics();
        }
      },
      load_analytics() {
         this.cookie_consent = JSON.parse(
           localStorage.getItem("cookie_consent")
         )
         if (!(this.cookie_consent === true)) {
           console.debug('Cookie consent not given or rejected.')
           return
         }

         console.debug('Cookie consent given. Enabling Google Analytics.')
         this.load_async_script(
           'https://www.googletagmanager.com/gtag/js?id=G-K49GTNVEK6'
         ).then(() => {
           // eslint-disable-next-line
           window.dataLayer = window.dataLayer || [];
           // eslint-disable-next-line
           function gtag(){dataLayer.push(arguments);}
           gtag('js', new Date());

           gtag('config', 'G-K49GTNVEK6');
         })
      },
      load_async_script(src) {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script')
          script.src = src
          script.async = true
          script.onload = () => resolve()
          script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
          document.head.appendChild(script)
        })
      }
    },
    mounted() {
      this.load_analytics()

      Hub.listen('auth', ({ payload }) => {
        switch (payload.event) {
          case 'signedIn':
            window.location.reload()
        }
      })
    },
    components: {
       NavBar
    }
  }
</script>

<style>
  /* ── Design tokens ── */
  :root {
    --bg-primary: #000000;
    --bg-surface: #0d1117;
    --bg-surface-raised: #161b22;
    --border-default: #30363d;
    --border-muted: #21262d;
    --text-primary: #f0f6fc;
    --text-secondary: #8b949e;
    --text-link: #58a6ff;
    --accent: #58a6ff;
    --accent-hover: #79c0ff;
    --danger: #f85149;
    --success: #3fb950;
    --warning: #d29922;
    --radius-sm: 6px;
    --radius-md: 8px;
  }

  /* ── Reset & base ── */
  *, *::before, *::after {
    border-color: var(--border-default);
  }

  html, body {
    background-color: var(--bg-primary) !important;
    color: var(--text-primary) !important;
  }

  #app {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: var(--text-primary);
    background-color: var(--bg-primary);
    min-height: 100vh;
  }

  /* ── Typography ── */
  p, li, ul {
    font-family: "Roboto", sans-serif;
    color: var(--text-primary);
    line-height: 1.6;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: "Inter", sans-serif;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--text-primary);
  }

  a {
    color: var(--text-link);
    text-decoration: none;
    transition: color 0.15s ease;
  }
  a:hover {
    color: var(--accent-hover);
  }

  /* ── Global card surface ── */
  .md {
    text-align: left;
    background-color: var(--bg-surface);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
  }

  /* ── Buttons ── */
  .btn-outline-light {
    color: var(--text-primary) !important;
    border-color: var(--border-default) !important;
    background: transparent !important;
    font-family: "Inter", sans-serif;
    font-weight: 500;
    font-size: 14px;
    padding: 8px 16px;
    transition: all 0.15s ease;
  }
  .btn-outline-light:hover {
    background-color: var(--bg-surface-raised) !important;
    border-color: var(--text-secondary) !important;
    color: #fff !important;
  }

  .btn-dark {
    background-color: var(--bg-surface-raised) !important;
    border-color: var(--border-default) !important;
    color: var(--text-primary) !important;
    font-family: "Inter", sans-serif;
    font-weight: 500;
  }
  .btn-dark:hover {
    background-color: var(--border-default) !important;
  }

  /* ── Form controls ── */
  .form-control {
    background-color: var(--bg-primary) !important;
    border: 1px solid var(--border-default) !important;
    color: var(--text-primary) !important;
    font-family: "Roboto", sans-serif;
    border-radius: var(--radius-sm) !important;
    padding: 10px 12px !important;
  }
  .form-control:focus {
    border-color: var(--accent) !important;
    box-shadow: 0 0 0 3px rgba(88, 166, 255, 0.15) !important;
  }
  .form-control::placeholder {
    color: var(--text-secondary) !important;
  }

  /* ── Alerts ── */
  .alert-warning {
    background-color: rgba(210, 153, 34, 0.1) !important;
    border-color: rgba(210, 153, 34, 0.3) !important;
    color: var(--warning) !important;
  }
  .alert-danger {
    background-color: rgba(248, 81, 73, 0.1) !important;
    border-color: rgba(248, 81, 73, 0.3) !important;
    color: var(--danger) !important;
  }
  .alert-success {
    background-color: rgba(63, 185, 80, 0.1) !important;
    border-color: rgba(63, 185, 80, 0.3) !important;
    color: var(--success) !important;
  }
  .alert-light {
    background-color: var(--bg-surface-raised) !important;
    border-color: var(--border-default) !important;
    color: var(--text-secondary) !important;
  }

  /* ── Tables ── */
  .table-dark {
    --bs-table-bg: var(--bg-surface) !important;
    --bs-table-border-color: var(--border-default) !important;
    --bs-table-hover-bg: var(--bg-surface-raised) !important;
    color: var(--text-primary) !important;
  }

  /* ── Spinners ── */
  .spinner-border {
    color: var(--text-secondary) !important;
  }

  /* ── Dropdown ── */
  .dropdown-menu {
    background-color: var(--bg-surface) !important;
    border: 1px solid var(--border-default) !important;
    border-radius: var(--radius-sm) !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5) !important;
  }
  .dropdown-item {
    color: var(--text-primary) !important;
    font-family: "Roboto", sans-serif;
    padding: 8px 16px !important;
  }
  .dropdown-item:hover, .dropdown-item:focus {
    background-color: var(--bg-surface-raised) !important;
    color: #fff !important;
  }

  /* ── Footer ── */
  .app-footer {
    margin-top: 3rem;
  }
  .footer-divider {
    height: 1px;
    background-color: var(--border-default);
  }
  .footer-dot {
    color: var(--text-secondary);
    margin: 0 8px;
  }
  .small-link {
    font-size: 12px;
    color: var(--text-secondary);
    font-family: "Inter", sans-serif;
    transition: color 0.15s ease;
  }
  .small-link:hover {
    color: var(--text-primary);
  }

  /* ── Cookie banner ── */
  .cookie-banner {
    background-color: var(--bg-surface);
    border-top: 1px solid var(--border-default);
    padding: 0;
  }
  .cookie-inner {
    max-width: 960px;
    margin: 0 auto;
    padding: 16px 24px;
  }
  .cookie-text {
    font-family: "Roboto", sans-serif;
    font-size: 15px;
    color: var(--text-secondary);
  }
  .btn-cookie-accept {
    background-color: var(--success) !important;
    border: none !important;
    color: #000 !important;
    font-family: "Inter", sans-serif;
    font-weight: 600;
    font-size: 14px;
    padding: 8px 20px;
    border-radius: var(--radius-sm);
  }
  .btn-cookie-accept:hover {
    background-color: #2ea043 !important;
  }
  .btn-cookie-decline {
    background-color: transparent !important;
    border: 1px solid var(--border-default) !important;
    color: var(--text-secondary) !important;
    font-family: "Inter", sans-serif;
    font-weight: 500;
    font-size: 14px;
    padding: 8px 20px;
    border-radius: var(--radius-sm);
  }
  .btn-cookie-decline:hover {
    border-color: var(--text-secondary) !important;
    color: var(--text-primary) !important;
  }

  /* ── Misc ── */
  .left-aligned-col {
    text-align: left;
  }
  .centered-col {
    text-align: center;
  }
  .centered-span {
    font-size: 20pt;
    vertical-align: middle;
  }
  .settings-box {
    background-color: var(--bg-surface);
    border: 1px solid var(--border-default);
    align-items: center;
    display: flex;
    justify-content: center;
  }

  /* ── Amplify Authenticator overrides ── */
  [data-amplify-authenticator] {
    --amplify-colors-background-primary: var(--bg-primary);
    --amplify-colors-background-secondary: var(--bg-surface);
    --amplify-colors-brand-primary-10: var(--accent);
    --amplify-colors-brand-primary-80: var(--accent);
    --amplify-colors-brand-primary-90: var(--accent);
    --amplify-colors-brand-primary-100: var(--accent);
    --amplify-colors-font-interactive: var(--accent);
    --amplify-colors-font-primary: var(--text-primary);
    --amplify-colors-font-secondary: var(--text-secondary);
    --amplify-components-authenticator-router-border-color: var(--border-default);
    --amplify-components-authenticator-router-background-color: var(--bg-surface);
    --amplify-components-fieldcontrol-color: var(--text-primary);
    --amplify-components-fieldcontrol-border-color: var(--border-default);
    --amplify-components-field-label-color: var(--text-primary);
  }

  [data-amplify-authenticator] input,
  [data-amplify-authenticator] .amplify-input {
    color: var(--text-primary) !important;
  }
</style>
